(function () {
  'use strict';

  var VERSION = '2026-09-02-aap-gallery-independent-video';
  var ROOT_SELECTOR = '[data-centauri-aap-gallery]';
  var TRACK_SELECTOR = '[data-centauri-aap-track]';
  var PANEL_SELECTOR = '[data-centauri-aap-panel]';
  var CONTROLS_SELECTOR = '[data-centauri-aap-controls]';
  var DOTNAV_SELECTOR = '[data-centauri-aap-dotnav]';
  var TOGGLE_SELECTOR = '[data-centauri-aap-toggle]';
  var ACTIVE_CLASS = 'current';
  var DEFAULT_DURATION = 6250;

  if (window.CentauriAAPGallery && window.CentauriAAPGallery.version === VERSION) {
    window.CentauriAAPGallery.init(document);
    return;
  }

  function matches(node, selector) {
    if (!node || node.nodeType !== 1) return false;
    var fn = node.matches || node.msMatchesSelector || node.webkitMatchesSelector;
    return fn ? fn.call(node, selector) : false;
  }

  function toArray(list) {
    return Array.prototype.slice.call(list || []);
  }

  function reduceMotion() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  /* C13 deliberately removes touch-horizontal paging on mobile and shows one
     card at a time. Hidden sibling cards have no useful layout geometry, so
     resize/image-load reconciliation must retain the controller's active
     index instead of recalculating it from zero-width siblings. */
  function isMobileSingleCard(state) {
    return !!(state && state.root && state.root.hasAttribute('data-aap-mobile-single-card') &&
      window.matchMedia && window.matchMedia('(max-width: 749px)').matches);
  }

  // Reduced motion suppresses AUTOMATIC playback, but an explicit user click on
  // the Play button is consent to animate and must override the preference.
  function motionAllowed(state) {
    return !reduceMotion() || !!(state && state.userPlayed);
  }

  function clamp(value, min, max) {
    return Math.max(min, Math.min(max, value));
  }

  function clampIndex(index, count) {
    return clamp(Number(index) || 0, 0, Math.max(0, count - 1));
  }

  function now() {
    return window.performance && typeof window.performance.now === 'function' ? window.performance.now() : Date.now();
  }

  function readDuration(root) {
    var value = parseFloat(root.getAttribute('data-aap-duration') || '');
    if (!value || value < 0) return DEFAULT_DURATION;
    return value < 100 ? value * 1000 : value;
  }

  function findRoots(scope) {
    var target = scope && scope.querySelectorAll ? scope : document;
    var roots = [];
    if (matches(scope, ROOT_SELECTOR)) roots.push(scope);
    return roots.concat(toArray(target.querySelectorAll(ROOT_SELECTOR)));
  }

  function targetOffset(state, index) {
    var panel = state.panels[clampIndex(index, state.count)];
    if (!panel || !state.track) return 0;
    var idealOffset = Math.round(panel.offsetLeft + panel.offsetWidth / 2 - state.track.clientWidth / 2);
    var maxOffset = Math.max(0, state.track.scrollWidth - state.track.clientWidth);
    return clamp(idealOffset, 0, maxOffset);
  }

  function nearestIndex(state) {
    if (!state || !state.track || !state.count) return 0;
    if (isMobileSingleCard(state)) return state.activeIndex;
    var current = state.track.scrollLeft;
    var bestIndex = 0;
    var bestDistance = Infinity;

    for (var i = 0; i < state.count; i++) {
      var distance = Math.abs(targetOffset(state, i) - current);
      if (distance < bestDistance) {
        bestDistance = distance;
        bestIndex = i;
      }
    }

    return bestIndex;
  }

  function setRootProgress(state, value) {
    var progress = clamp(value, 0, Math.max(0, state.count - 1));
    state.root.style.setProperty('--autoplay-progress', String(progress));
  }

  function setDotProgress(state, value) {
    state.currentProgress = clamp(value, 0, 1);

    for (var i = 0; i < state.dots.length; i++) {
      state.dots[i].style.setProperty('--aap-dot-progress', i === state.activeIndex ? String(state.currentProgress) : '0');
    }

    setRootProgress(state, state.activeIndex + state.currentProgress);
  }

  function readLabel(panel, index) {
    return panel.getAttribute('data-aap-label') ||
      panel.getAttribute('data-tab-label') ||
      panel.getAttribute('aria-label') ||
      'Highlight ' + (index + 1);
  }

  function updatePlaybackClasses(state) {
    var playing = state.playing && !state.ended;
    var paused = !state.playing && !state.ended;
    var ended = state.ended;

    state.root.classList.toggle('playing', playing);
    state.root.classList.toggle('paused', paused);
    state.root.classList.toggle('ended', ended);
    state.root.classList.toggle('is-ready', true);
    state.root.classList.toggle('is-activated', true);

    if (state.controls) {
      state.controls.classList.toggle('playing', playing);
      state.controls.classList.toggle('paused', paused);
      state.controls.classList.toggle('ended', ended);
      state.controls.classList.toggle('activated', true);
      state.controls.classList.toggle('is-activated', true);
      state.controls.classList.remove('inactive');
      state.controls.setAttribute('data-aap-state', ended ? 'ended' : playing ? 'playing' : 'paused');
    }

    if (state.toggle) {
      var label = state.toggle.getAttribute(ended ? 'data-aria-replay' : playing ? 'data-aria-pause' : 'data-aria-play');
      if (label) state.toggle.setAttribute('aria-label', label);
      state.toggle.setAttribute('aria-pressed', playing ? 'true' : 'false');
    }
  }

  function updateMediaPlayback(state) {
    var allowPlay = state.playing && !state.ended && state.inView && motionAllowed(state);

    for (var i = 0; i < state.panels.length; i++) {
      var videos = state.panels[i].querySelectorAll('video');
      for (var j = 0; j < videos.length; j++) {
        var video = videos[j];
        if (video.closest && video.closest('[data-centauri-independent-video]')) continue;
        if (allowPlay && i === state.activeIndex && typeof video.play === 'function') {
          video.muted = true;
          video.playsInline = true;
          var promise = video.play();
          if (promise && typeof promise.catch === 'function') promise.catch(function () {});
        } else if (typeof video.pause === 'function') {
          video.pause();
        }
      }
    }
  }

  function cancelProgressFrame(state) {
    if (state.progressFrame) {
      window.cancelAnimationFrame(state.progressFrame);
      state.progressFrame = 0;
    }
  }

  function syncProgressFromClock(state) {
    if (!state.playing || state.ended || !motionAllowed(state)) return;
    var elapsed = now() - state.progressStartedAt;
    setDotProgress(state, state.progressBase + elapsed / state.duration);
  }

  function resetProgress(state) {
    state.progressBase = 0;
    state.progressStartedAt = now();
    setDotProgress(state, 0);
  }

  function setEnded(state) {
    if (!state || state.destroyed) return;
    cancelProgressFrame(state);
    state.playing = false;
    state.ended = true;
    state.userPaused = false;
    setDotProgress(state, 1);
    updatePlaybackClasses(state);
    updateMediaPlayback(state);
  }

  function runProgress(state) {
    cancelProgressFrame(state);
    if (!state.playing || state.ended || !motionAllowed(state) || !state.inView) return;

    var tick = function () {
      if (state.destroyed || !state.playing || state.ended || !motionAllowed(state) || !state.inView) {
        state.progressFrame = 0;
        return;
      }

      syncProgressFromClock(state);

      if (state.currentProgress >= 1) {
        if (state.activeIndex >= state.count - 1) {
          setEnded(state);
        } else {
          goToState(state, state.activeIndex + 1, { behavior: 'smooth', resetProgress: true, source: 'auto' });
        }
        return;
      }

      state.progressFrame = window.requestAnimationFrame(tick);
    };

    state.progressStartedAt = now();
    state.progressFrame = window.requestAnimationFrame(tick);
  }

  function playState(state, options) {
    if (!state || state.destroyed) return;
    options = options || {};
    // An explicit Play click (user: true) is consent to animate and overrides
    // the reduced-motion preference; automatic playback still honors it.
    if (options.user) state.userPlayed = true;
    if (reduceMotion() && !state.userPlayed) {
      pauseState(state, { user: options.user });
      return;
    }

    if (state.ended || state.currentProgress >= 1) {
      resetProgress(state);
    }

    state.playing = true;
    state.ended = false;
    if (options.user) state.userPaused = false;
    state.progressStartedAt = now();
    state.progressBase = state.currentProgress;
    updatePlaybackClasses(state);
    updateMediaPlayback(state);
    runProgress(state);
  }

  function pauseState(state, options) {
    if (!state || state.destroyed) return;
    options = options || {};
    syncProgressFromClock(state);
    cancelProgressFrame(state);
    state.playing = false;
    state.ended = false;
    if (options.user) state.userPaused = true;
    state.progressBase = state.currentProgress;
    updatePlaybackClasses(state);
    updateMediaPlayback(state);
  }

  function activate(state, index, options) {
    if (!state || state.destroyed || !state.count) return;
    options = options || {};
    index = clampIndex(index, state.count);
    var changed = index !== state.activeIndex;

    state.activeIndex = index;
    state.root.setAttribute('data-centauri-active-index', String(index));
    state.root.setAttribute('data-aap-active-index', String(index));
    if (state.section) state.section.setAttribute('data-centauri-active-index', String(index));

    for (var i = 0; i < state.count; i++) {
      var isActive = i === index;
      var panel = state.panels[i];
      var dot = state.dots[i];
      var item = state.dotItems[i];

      if (panel) {
        panel.classList.toggle(ACTIVE_CLASS, isActive);
        panel.classList.toggle('is-active', isActive);
        panel.setAttribute('tabindex', isActive ? '0' : '-1');
      }

      if (dot) {
        dot.classList.toggle(ACTIVE_CLASS, isActive);
        dot.classList.toggle('is-active', isActive);
        dot.setAttribute('aria-selected', isActive ? 'true' : 'false');
        dot.setAttribute('tabindex', isActive ? '0' : '-1');
      }

      if (item) {
        item.classList.toggle(ACTIVE_CLASS, isActive);
        item.classList.toggle('is-active', isActive);
      }
    }

    if (changed && options.resetProgress !== false) resetProgress(state);
    else setDotProgress(state, state.currentProgress);

    updatePlaybackClasses(state);
    updateMediaPlayback(state);
    if (options.focusDot && state.dots[index]) state.dots[index].focus();
  }

  function goToState(state, index, options) {
    if (!state || state.destroyed || !state.track) return;
    options = options || {};
    index = clampIndex(index, state.count);
    state.ended = false;
    activate(state, index, options);

    var behavior = options.behavior || (reduceMotion() ? 'auto' : 'smooth');
    var nextOffset = targetOffset(state, index);
    state.programmaticIndex = index;
    state.programmaticTarget = nextOffset;
    state.programmaticLastDistance = Math.abs(state.track.scrollLeft - nextOffset);
    state.programmaticScrollUntil = behavior === 'smooth' ? now() + 720 : now() + 80;
    state.track.scrollTo({ left: nextOffset, behavior: behavior });

    if (state.playing && !state.ended) {
      state.progressBase = state.currentProgress;
      state.progressStartedAt = now();
      runProgress(state);
    }
  }

  function replayState(state) {
    if (!state || state.destroyed) return;
    state.ended = false;
    goToState(state, 0, { behavior: reduceMotion() ? 'auto' : 'smooth', resetProgress: true, source: 'replay' });
    playState(state, { user: true });
  }

  function onKeydown(state, event) {
    var key = event.key;
    if (key === 'ArrowRight' || key === 'ArrowDown') {
      event.preventDefault();
      goToState(state, state.activeIndex + 1, { focusDot: true });
    } else if (key === 'ArrowLeft' || key === 'ArrowUp') {
      event.preventDefault();
      goToState(state, state.activeIndex - 1, { focusDot: true });
    } else if (key === 'Home') {
      event.preventDefault();
      goToState(state, 0, { focusDot: true });
    } else if (key === 'End') {
      event.preventDefault();
      goToState(state, state.count - 1, { focusDot: true });
    } else if (key === 'Enter' || key === ' ' || key === 'Spacebar') {
      event.preventDefault();
      goToState(state, state.activeIndex, { focusDot: false });
    }
  }

  function buildDotnav(state) {
    if (!state.dotnav) return;
    state.dotnav.innerHTML = '';
    state.dotnav.style.setProperty('--dotnav-count', String(state.count));
    state.dots = [];
    state.dotItems = [];

    for (var i = 0; i < state.count; i++) {
      var panel = state.panels[i];
      var panelId = panel.id || (state.uid + '-panel-' + i);
      var tabId = panel.getAttribute('data-tab-id') || (state.uid + '-tab-' + i);
      var label = readLabel(panel, i);
      var item = document.createElement('li');
      var link = document.createElement('a');
      var text = document.createElement('span');

      panel.id = panelId;
      panel.setAttribute('role', panel.getAttribute('role') || 'tabpanel');
      panel.setAttribute('aria-labelledby', tabId);

      item.className = 'dotnav-item aap-media-card-gallery__dotnav-item';
      item.setAttribute('role', 'presentation');
      item.style.setProperty('--item-index', String(i));

      link.className = 'dotnav-link';
      link.id = tabId;
      link.href = '#' + panelId;
      link.setAttribute('role', 'tab');
      link.setAttribute('aria-controls', panelId);
      link.setAttribute('aria-selected', 'false');
      link.setAttribute('tabindex', '-1');
      link.style.setProperty('--aap-dot-progress', '0');
      link.style.setProperty('--item-index', String(i));

      text.className = 'centauri-aap-visually-hidden';
      text.textContent = label;
      link.appendChild(text);

      (function (targetIndex) {
        link.addEventListener('click', function (event) {
          event.preventDefault();
          goToState(state, targetIndex, { behavior: reduceMotion() ? 'auto' : 'smooth', resetProgress: true, source: 'dot' });
        });
      })(i);

      item.appendChild(link);
      state.dotnav.appendChild(item);
      state.dotItems.push(item);
      state.dots.push(link);
    }

    var keyHandler = function (event) { onKeydown(state, event); };
    state.dotnav.addEventListener('keydown', keyHandler);
    state.cleanups.push(function () {
      state.dotnav.removeEventListener('keydown', keyHandler);
    });
  }

  function scheduleScrollUpdate(state) {
    if (state.scrollFrame) return;
    state.scrollFrame = window.requestAnimationFrame(function () {
      state.scrollFrame = 0;
      if (state.programmaticScrollUntil) {
        var distance = Math.abs(state.track.scrollLeft - state.programmaticTarget);
        var movingCloser = distance <= state.programmaticLastDistance + 2;
        if (distance <= 2) {
          state.programmaticScrollUntil = 0;
        } else if (now() < state.programmaticScrollUntil || movingCloser) {
          state.programmaticLastDistance = distance;
          state.programmaticScrollUntil = now() + 160;
          return;
        } else {
          state.programmaticScrollUntil = 0;
        }
      }
      var next = nearestIndex(state);
      if (next !== state.activeIndex) {
        activate(state, next, { resetProgress: true, source: 'scroll' });
        if (state.playing && !state.ended) runProgress(state);
      }
    });
  }

  function setupTrackEvents(state) {
    var scrollHandler = function () {
      scheduleScrollUpdate(state);
    };
    state.track.addEventListener('scroll', scrollHandler, { passive: true });
    state.cleanups.push(function () {
      state.track.removeEventListener('scroll', scrollHandler);
    });

    var keyHandler = function (event) {
      if (event.target && event.target.closest && event.target.closest('button, a, input, select, textarea, [role="button"]')) return;
      onKeydown(state, event);
    };
    state.track.addEventListener('keydown', keyHandler);
    state.cleanups.push(function () {
      state.track.removeEventListener('keydown', keyHandler);
    });

  }

  function setupControlEvents(state) {
    if (!state.toggle) return;

    var toggleHandler = function () {
      if (state.ended) {
        replayState(state);
      } else if (state.playing) {
        pauseState(state, { user: true });
      } else {
        playState(state, { user: true });
      }
    };
    state.toggle.addEventListener('click', toggleHandler);
    state.cleanups.push(function () {
      state.toggle.removeEventListener('click', toggleHandler);
    });
  }

  function setupAssetEvents(state) {
    var nodes = state.root.querySelectorAll('img, video');
    for (var i = 0; i < nodes.length; i++) {
      (function (node) {
        var eventName = node.tagName && node.tagName.toLowerCase() === 'video' ? 'loadedmetadata' : 'load';
        var handler = function () {
          if (!state.destroyed) scheduleScrollUpdate(state);
        };
        node.addEventListener(eventName, handler, { once: true });
        state.cleanups.push(function () {
          node.removeEventListener(eventName, handler);
        });
      })(nodes[i]);
    }
  }

  function setupResize(state) {
    var resizeHandler = function () {
      window.clearTimeout(state.resizeTimer);
      state.resizeTimer = window.setTimeout(function () {
        if (state.destroyed) return;
        goToState(state, state.activeIndex, { behavior: 'auto', resetProgress: false, source: 'resize' });
        scheduleScrollUpdate(state);
      }, 120);
    };
    window.addEventListener('resize', resizeHandler, { passive: true });
    state.cleanups.push(function () {
      window.removeEventListener('resize', resizeHandler);
    });
  }

  function setupVisibility(state) {
    var hiddenHandler = function () {
      if (document.hidden) {
        state.wasPlayingBeforeHidden = state.playing;
        if (state.playing) pauseState(state, { user: false });
      } else if (state.wasPlayingBeforeHidden && !state.userPaused) {
        playState(state, { user: false });
      }
    };
    document.addEventListener('visibilitychange', hiddenHandler);
    state.cleanups.push(function () {
      document.removeEventListener('visibilitychange', hiddenHandler);
    });

    if ('IntersectionObserver' in window) {
      var observer = new IntersectionObserver(function (entries) {
        var entry = entries[0];
        state.inView = !!(entry && entry.isIntersecting);
        updateMediaPlayback(state);
        if (state.inView && state.playing) runProgress(state);
      }, { threshold: 0.18 });
      observer.observe(state.root);
      state.cleanups.push(function () {
        observer.disconnect();
      });
    }
  }

  function setup(root) {
    if (!root) return null;
    if (root.centauriAAPState && root.centauriAAPState.version === VERSION) return root.centauriAAPState;

    var track = root.querySelector(TRACK_SELECTOR);
    var panels = toArray(root.querySelectorAll(PANEL_SELECTOR));
    var controls = root.querySelector(CONTROLS_SELECTOR);
    var dotnav = controls ? controls.querySelector(DOTNAV_SELECTOR) : root.querySelector(DOTNAV_SELECTOR);
    if (!track || !panels.length || !controls || !dotnav) return null;

    var state = {
      version: VERSION,
      uid: 'centauri-aap-' + Math.random().toString(36).slice(2, 9),
      root: root,
      section: root.closest ? root.closest('[data-centauri-section], .shopify-section, .centauri-section') : null,
      track: track,
      panels: panels,
      controls: controls,
      dotnav: dotnav,
      toggle: controls.querySelector(TOGGLE_SELECTOR),
      dots: [],
      dotItems: [],
      count: panels.length,
      activeIndex: 0,
      duration: readDuration(root),
      cleanups: [],
      destroyed: false,
      playing: false,
      ended: false,
      userPaused: false,
      userPlayed: false,
      currentProgress: 0,
      progressBase: 0,
      progressStartedAt: now(),
      progressFrame: 0,
      scrollFrame: 0,
      resizeTimer: 0,
      inView: true,
      wasPlayingBeforeHidden: false
    };

    root.centauriAAPState = state;
    root.setAttribute('data-aap-count', String(state.count));
    controls.style.setProperty('--aap-count', String(state.count));

    buildDotnav(state);
    setupTrackEvents(state);
    setupControlEvents(state);
    setupAssetEvents(state);
    setupResize(state);
    setupVisibility(state);
    activate(state, nearestIndex(state), { resetProgress: true });

    if (!reduceMotion()) playState(state, { user: false });
    else pauseState(state, { user: false });

    return state;
  }

  function teardownRoot(root) {
    var state = root && root.centauriAAPState;
    if (!state) return;

    state.destroyed = true;
    window.clearTimeout(state.resizeTimer);
    cancelProgressFrame(state);
    if (state.scrollFrame) window.cancelAnimationFrame(state.scrollFrame);

    while (state.cleanups.length) {
      var cleanup = state.cleanups.pop();
      cleanup();
    }

    updateMediaPlayback(state);

    if (state.dotnav) {
      state.dotnav.innerHTML = '';
      state.dotnav.style.removeProperty('--dotnav-count');
    }

    for (var i = 0; i < state.panels.length; i++) {
      state.panels[i].classList.remove(ACTIVE_CLASS, 'is-active');
      state.panels[i].removeAttribute('tabindex');
    }

    state.root.classList.remove('playing', 'paused', 'ended', 'is-ready', 'is-activated');
    state.root.removeAttribute('data-aap-count');
    state.root.removeAttribute('data-aap-active-index');
    state.root.removeAttribute('data-centauri-active-index');
    state.root.style.removeProperty('--autoplay-progress');

    if (state.controls) {
      state.controls.classList.remove('playing', 'paused', 'ended', 'activated', 'is-activated');
      state.controls.classList.add('inactive');
      state.controls.removeAttribute('data-aap-state');
      state.controls.style.removeProperty('--aap-count');
    }

    root.centauriAAPState = null;
  }

  function init(scope) {
    var roots = findRoots(scope || document);
    for (var i = 0; i < roots.length; i++) setup(roots[i]);
  }

  function resolveState(target) {
    if (target && target.version === VERSION && target.root) return target;
    if (typeof target === 'string') target = document.querySelector(target);
    if (!target || target === document || target === window) target = document.querySelector(ROOT_SELECTOR);
    if (!target) return null;
    var root = matches(target, ROOT_SELECTOR) ? target : target.closest && target.closest(ROOT_SELECTOR);
    return root ? setup(root) : null;
  }

  function findPanelByBlockEvent(event) {
    var detail = event && event.detail;
    var blockId = detail && detail.blockId;
    var panels;
    var i;

    if (blockId) {
      panels = document.querySelectorAll('[data-aap-block-id], [data-centauri-c13-block-id]');
      for (i = 0; i < panels.length; i++) {
        if (panels[i].getAttribute('data-aap-block-id') === blockId || panels[i].getAttribute('data-centauri-c13-block-id') === blockId) {
          return panels[i];
        }
      }
    }

    var target = event && event.target;
    if (matches(target, PANEL_SELECTOR)) return target;
    return target && target.querySelector ? target.querySelector(PANEL_SELECTOR) : null;
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(document); });
  } else {
    init(document);
  }

  document.addEventListener('shopify:section:load', function (event) {
    init(event.target || document);
  });

  document.addEventListener('shopify:section:unload', function (event) {
    var roots = findRoots(event.target || document);
    for (var i = 0; i < roots.length; i++) teardownRoot(roots[i]);
  });

  document.addEventListener('shopify:block:select', function (event) {
    init(document);
    var panel = findPanelByBlockEvent(event);
    if (!panel) return;
    var state = resolveState(panel);
    if (!state) return;
    var index = state.panels.indexOf(panel);
    if (index >= 0) goToState(state, index, { behavior: reduceMotion() ? 'auto' : 'smooth', focusDot: true, resetProgress: true, source: 'shopify' });
  });

  window.CentauriAAPGallery = {
    version: VERSION,
    init: init,
    teardown: function (target) {
      var state = resolveState(target);
      if (state) teardownRoot(state.root);
    },
    goTo: function (target, index) {
      if (typeof index === 'undefined') {
        index = target;
        target = null;
      }
      var state = resolveState(target);
      if (state) goToState(state, index, { behavior: reduceMotion() ? 'auto' : 'smooth', resetProgress: true, source: 'api' });
    },
    play: function (target) {
      var state = resolveState(target);
      if (state) playState(state, { user: true });
    },
    pause: function (target) {
      var state = resolveState(target);
      if (state) pauseState(state, { user: true });
    },
    replay: function (target) {
      var state = resolveState(target);
      if (state) replayState(state);
    }
  };

  window.CentauriC13ScrollTabs = {
    version: VERSION,
    init: init,
    teardown: function (target) {
      var state = resolveState(target);
      if (state) teardownRoot(state.root);
    },
    goTo: function (target, index) {
      if (typeof index === 'undefined') {
        index = target;
        target = null;
      }
      var state = resolveState(target);
      if (state) goToState(state, index, { behavior: reduceMotion() ? 'auto' : 'smooth', resetProgress: true, source: 'api' });
    }
  };
})();
